<template>
  <div class="gpa-shared-garden">
    <div id="map" class="gpa-shared-garden__map" :style="mapStyle">
      <gp-map-controls :garden-map="gardenMap" />
    </div>

    <rui-panel class="gpa-shared-garden__panel" :class="{'gpa-shared-garden__panel--force-visible': showPanel}">
      <rui-tabs>
        <rui-button icon="layers" :active="sidePanelTab === 'layers'" @click="sidePanelTab = 'layers'">
          {{ $t('js.layers.title') }}
        </rui-button>
        <rui-button icon="info" :active="sidePanelTab === 'legend'" @click="sidePanelTab = 'legend'">
          {{ $t('js.shared_garden.legend.title') }}
        </rui-button>
      </rui-tabs>

      <gpa-garden-layers v-show="sidePanelTab === 'layers'"
                         :visible-layers="activeLayers"
                         @toggle="handleLayerVisibilityToggle" />

      <gpa-garden-legend v-show="sidePanelTab === 'legend'" />
    </rui-panel>
  </div>

  <rui-button icon="info"
              variant="helping"
              size="large"
              class="gpa-shared-garden__panel-trigger"
              @click="showPanel = !showPanel" />

  <div id="garden-popup">
    <!-- Popup for paths -->
    <!-- Custom "mouse-xxx" events as they are not attached otherwise -->
    <gpa-path-popup v-if="pointedEntityType === 'path'"
                    :path="pointedEntity"
                    readonly
                    @close="gardenMap.review()" />
  </div>

  <!-- Drawer for patches -->
  <gpa-patch-drawer v-if="pointedEntityType === 'patch'"
                    :patch="pointedEntity"
                    @close="gardenMap.review()" />

  <gpa-element-drawer v-if="pointedEntityType === 'element'"
                      :element="pointedEntity"
                      @close="gardenMap.review()" />

  <gpa-element-tooltip :element="hoveredElement" />
  <gpa-patch-tooltip :patch="hoveredPatch" />
  <gpa-path-tooltip :path="hoveredPath" />

  <rui-loading v-if="!mapIsReady" fill>{{ $t('js.garden.loading_map') }}</rui-loading>
</template>

<script>
import RuiButton from '@experiments-labs/rise_ui/src/components/Button/Button.vue'
import RuiLoading from '@experiments-labs/rise_ui/src/components/Loading/Loading.vue'
import RuiPanel from '@experiments-labs/rise_ui/src/components/Panel/Panel.vue'
import RuiTabs from '@experiments-labs/rise_ui/src/components/Tabs/Tabs.vue'

import GardenMap from '../../../classes/GardenMap'

import GpMapControls from '../../../common/MapControls/MapControls.vue'

import GpaElementTooltip from '../../garden/components/Elements/_Tooltip.vue'
import GpaPatchTooltip from '../../garden/components/Patches/_Tooltip.vue'
import GpaPathPopup from '../../garden/components/Paths/_Popup.vue'
import GpaPathTooltip from '../../garden/components/Paths/_Tooltip.vue'
import GpaElementDrawer from './_ElementDrawer.vue'
import GpaGardenLayers from './_Layers.vue'
import GpaGardenLegend from './_Legend.vue'
import GpaPatchDrawer from './_PatchDrawer.vue'

/**
 * @typedef {import('../../../classes/models/Path')} Path
 * @typedef {import('../../../classes/models/Patch')} Patch
 * @typedef {import('../../../classes/models/Element')} Element
 */

export default {
  name: 'GpaSharedMap',
  components: {
    RuiLoading,
    RuiButton,
    RuiTabs,
    RuiPanel,
    GpMapControls,
    GpaPathTooltip,
    GpaPatchTooltip,
    GpaElementTooltip,
    GpaElementDrawer,
    GpaGardenLayers,
    GpaGardenLegend,
    GpaPatchDrawer,
    GpaPathPopup,
  },
  data () {
    return {
      /** @type {GardenMap} Map instance */
      gardenMap: null,
      /** @type {number[]} List of active layers */
      activeLayers: [],
      /** @type {Patch|Path} Active entity in review mode */
      pointedEntity: null,
      /** @type {Patch|Path|Element} Entity under cursor */
      hoveredEntity: null,
      /** @type {string|null} Active entity type in review mode */
      pointedEntityType: null,
      /** @type {boolean} Whether or not the map is loaded */
      mapIsReady: false,

      showPanel: false,

      // Side panel
      sidePanelTab: 'layers',
    }
  },
  computed: {
    map () { return this.$store.getters.currentMap },
    mapStyle () {
      if (!this.map.backgroundColor) return ''

      return `background-color: ${this.map.backgroundColor}; background-image: none`
    },
    hoveredElement () { return this.hoveredEntity?.className === 'Element' ? this.hoveredEntity : null },
    hoveredPatch () { return this.hoveredEntity?.className === 'Patch' ? this.hoveredEntity : null },
    hoveredPath () { return this.hoveredEntity?.className === 'Path' ? this.hoveredEntity : null },
  },
  methods: {
    handleLayerVisibilityToggle (id) {
      const index = this.activeLayers.indexOf(id)
      if (index === -1) {
        this.activeLayers.push(id)
      } else {
        this.activeLayers.splice(index, 1)
      }

      this.gardenMap.setVisibleLayers(this.activeLayers)
    },
    handlePointEntity (event) {
      if (!event.detail) {
        this.pointedEntity = null
        this.pointedEntityType = null
        return
      }
      const type = event.detail.type
      const id = event.detail.id
      this.pointedEntityType = type
      this.pointedEntity = id ? this.$store.getters[type](id) : null
    },
    handleHoverEntity (event) {
      if (!event.detail) {
        this.hoveredEntity = null
        return
      }

      this.hoveredEntity = event.detail.id ? this.$store.getters[event.detail.type](event.detail.id) : null
    },
  },

  created () {
    this.$nextTick(() => {
      this.gardenMap = new GardenMap(this.map, 'map', 'garden-popup',)

      this.gardenMap.mapElement.addEventListener('ready', () => { this.mapIsReady = true })
      this.gardenMap.mapElement.addEventListener('point-entity', this.handlePointEntity)
      this.gardenMap.mapElement.addEventListener('hover-entity', this.handleHoverEntity)

      this.gardenMap.review()
      this.activeLayers = this.$store.getters.layers.map(l => l.id)
    })
  },
}
</script>
